import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import B2BService from '../../API/B2BService';
import AuthService from '../../API/AuthService';
import useUserStore from '../../store/userStore';
import usePageMeta from '../../hooks/usePageMeta';
import { mdToHtml } from '../../utils/markdown';
import { IconArrowLeft, IconClock, IconEye, IconArrowRight } from '../../components/icons';
import Spinner from '../../components/Spinner';
import cl from './ArticleView.module.css';

// Страница одной статьи: заголовок, обложка, тело (markdown → html),
// статистика и комментарии. Комментировать могут только залогиненные
// b2b-клиенты (с записью в b2b_centres). Незалогиненным показываем
// «Войдите чтобы комментировать».
export default function ArticleView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { profile, centre } = useUserStore();

    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    const [commentText, setCommentText] = useState('');
    const [posting, setPosting] = useState(false);
    const [postErr, setPostErr] = useState('');

    const isAuthed = AuthService.isAuthenticated();
    const canComment = isAuthed && !!centre;  // центр зарегистрирован

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    async function load() {
        setLoading(true);
        setErr('');
        const data = await B2BService.getArticle(id);
        if (!data) {
            setErr('Статья не найдена');
        } else {
            setArticle(data);
        }
        setLoading(false);
    }

    usePageMeta({
        title: article ? `${article.title} · СтартКласс` : 'Статья · СтартКласс',
        description: article?.summary || 'Статья из раздела «Интеграция» СтартКласс.',
    });

    async function onSubmit(e) {
        e.preventDefault();
        if (!canComment) return;
        const t = commentText.trim();
        if (t.length < 3) { setPostErr('Минимум 3 символа'); return; }
        setPostErr('');
        setPosting(true);
        const res = await B2BService.addComment(id, t);
        setPosting(false);
        if (res.ok && res.comment) {
            // Локально добавляем коммент, чтобы не перезагружать статью
            setArticle(a => ({
                ...a,
                comments: [...(a.comments || []), res.comment],
                comments_count: (a.comments_count || 0) + 1,
            }));
            setCommentText('');
        } else {
            setPostErr(res.message);
        }
    }

    if (loading) {
        return (
            <main className={cl.page}>
                <Spinner size="large" text="Загружаем статью" fullPage center/>
            </main>
        );
    }

    if (err || !article) {
        return (
            <main className={cl.page}>
                <Link to="/articles" className={cl.back}>
                    <IconArrowLeft width={14} height={14}/> К списку статей
                </Link>
                <div className={cl.empty}>{err || 'Статья не найдена'}</div>
            </main>
        );
    }

    return (
        <main className={cl.page}>
            <Link to="/articles" className={cl.back}>
                <IconArrowLeft width={14} height={14}/> Все статьи
            </Link>

            <article className={cl.article}>
                <div className={cl.head}>
                    <span className={cl.eyebrow}>
                        {article.category?.title || 'Интеграция'}
                    </span>
                    <h1 className={cl.title}>{article.title}</h1>
                    {article.summary && (
                        <p className={cl.summary}>{article.summary}</p>
                    )}
                    <div className={cl.meta}>
                        <span><IconClock width={13} height={13}/> {article.estimated_read_time} мин чтения</span>
                        <span><IconEye width={13} height={13}/> {article.views_count} просмотров</span>
                        <span>{article.comments_count} комментариев</span>
                    </div>
                </div>

                {article.featured_image && (
                    <img
                        className={cl.cover}
                        src={resolveImg(article.featured_image)}
                        alt={article.title}
                        loading="lazy"
                    />
                )}

                <div
                    className={cl.body}
                    dangerouslySetInnerHTML={{ __html: mdToHtml(rewriteImageUrls(article.content)) }}
                />
            </article>

            <section className={cl.commentsSection}>
                <h2 className={cl.commentsTitle}>
                    Комментарии ({article.comments_count || 0})
                </h2>

                {canComment ? (
                    <form className={cl.form} onSubmit={onSubmit}>
                        <div className={cl.formAuthor}>
                            Вы пишете как <b>{centre?.name || profile?.firstName}</b>
                        </div>
                        <textarea
                            className={cl.textarea}
                            placeholder="Поделитесь мнением или задайте вопрос…"
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            rows={3}
                            maxLength={2000}
                        />
                        {postErr && <div className={cl.err}>{postErr}</div>}
                        <div className={cl.formActions}>
                            <span className={cl.charCount}>
                                {commentText.length}/2000
                            </span>
                            <button type="submit" className={cl.btn} disabled={posting || commentText.trim().length < 3}>
                                {posting ? 'Отправляем…' : (<>Отправить <IconArrowRight width={14} height={14}/></>)}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className={cl.loginCta}>
                        <p>
                            Чтобы оставить комментарий, <b>войдите</b> или
                            <b> зарегистрируйте центр</b>.
                        </p>
                        <div className={cl.loginActions}>
                            <button className={cl.btnGhost}
                                onClick={() => navigate('/login')}>
                                Войти
                            </button>
                            <button className={cl.btn}
                                onClick={() => navigate('/registration')}>
                                Зарегистрироваться
                            </button>
                        </div>
                    </div>
                )}

                <div className={cl.comments}>
                    {(article.comments || []).length === 0 ? (
                        <div className={cl.empty}>
                            Пока нет комментариев. Будьте первым!
                        </div>
                    ) : (
                        (article.comments || []).map(c => (
                            <CommentItem key={c.id} comment={c}/>
                        ))
                    )}
                </div>
            </section>
        </main>
    );
}


function CommentItem({ comment }) {
    return (
        <div className={cl.comment}>
            <div className={cl.commentHead}>
                <span className={cl.commentAuthor}>{comment.author_name || 'Пользователь'}</span>
                <span className={cl.commentDate}>{formatDate(comment.created_at)}</span>
            </div>
            <div className={cl.commentBody}>{comment.content}</div>
        </div>
    );
}


function resolveImg(path) {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return rewriteUrl(path);
    }
    if (path.startsWith('/')) return `https://minecraftia-school.ru/api-admin${path}`;
    return `https://minecraftia-school.ru/api-admin/assets/articles/${path}`;
}


/** Подмена «битых» URL картинок на стабильный путь через api-admin.
 *  https://adminpanelminecraftia.ru/assets/articles/file.jpg
 *  https://minecraftia-rus.online/assets/articles/file.jpg
 *  → https://minecraftia-school.ru/api-admin/assets/articles/file.jpg
 *
 *  Используется в content статьи (markdown), который тоже содержит
 *  старые домены. На бэке не правим — может быть другой сервис ссылается.
 */
function rewriteUrl(url) {
    return url
        .replace('https://adminpanelminecraftia.ru/assets/', 'https://minecraftia-school.ru/api-admin/assets/')
        .replace('http://adminpanelminecraftia.ru/assets/', 'https://minecraftia-school.ru/api-admin/assets/')
        .replace('https://minecraftia-rus.online/assets/', 'https://minecraftia-school.ru/api-admin/assets/');
}


/** Заменяет URL картинок в markdown-content. Применяем перед mdToHtml. */
function rewriteImageUrls(md) {
    if (!md) return '';
    return md.replace(
        /(!\[[^\]]*\]\()([^)]+)(\))/g,
        (_, prefix, url, suffix) => prefix + rewriteUrl(url.trim()) + suffix
    );
}


function formatDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('ru-RU', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}
